import { useCallback, useEffect, useRef, useState } from "react";
import {
  continueClassroomHandoverSetup,
  getClassroomHandoverSetup,
  inviteClassroomHandoverTeacher,
  searchClassroomHandoverTeachers,
} from "../services/APIClient";
import { getClassroomHandoverIdempotencyKey } from "../utils/classroomHandoverIdempotency";
export const normalizeTeacherAccount = (teacher) => ({
  id: teacher.teacherid || teacher.pendingteacherid || teacher.id,
  name: teacher.name,
  email: teacher.email || "",
  kind: teacher.source === "pendingteacher" || teacher.pendingteacherid ? "pending" : "active",
  status: teacher.accountStatus || (teacher.pendingteacherid ? "pending" : "active"),
});

export const normalizeClassroomHandoverSetup = (setup) => {
  if (!setup) return null;
  const previousClass = setup.previousClass || setup.sourceClass || {};
  const teacherName = previousClass.teacherName ||
    previousClass.teacher?.name ||
    previousClass.classOwner?.name ||
    setup.previousTeacherName ||
    setup.sourceTeacherName ||
    setup.previousTeacher?.name ||
    setup.historicalCue?.previousTeacherName ||
    "";

  return {
    ...setup,
    previousClass: {
      ...previousClass,
      teacherName,
    },
  };
};

export const getSetupContinueIdempotencyKey = (classId, version, payload) =>
  getClassroomHandoverIdempotencyKey(classId, "continue", version, payload);

const setupStatusFromError = (error) => {
  const status = error?.response?.status;
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 410) return "complete";
  return "error";
};

export default function useClassroomHandoverSetup({ schoolId, classId }) {
  const [entry, setEntry] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [conflictEntry, setConflictEntry] = useState(null);
  const attemptRef = useRef(null);

  const readSetup = useCallback(async () => {
    if (!classId || !schoolId) return null;
    return getClassroomHandoverSetup(schoolId, classId);
  }, [classId, schoolId]);

  const load = useCallback(async () => {
    if (!schoolId || !classId) {
      setEntry(null);
      setError(null);
      setConflictEntry(null);
      setStatus("idle");
      return null;
    }
    setStatus("loading");
    setError(null);
    try {
      const nextEntry = normalizeClassroomHandoverSetup(await readSetup());
      setEntry(nextEntry);
      setConflictEntry(null);
      setStatus(nextEntry?.setupStatus === "complete" ? "complete" : "ready");
      return nextEntry;
    } catch (nextError) {
      setError(nextError);
      setStatus(setupStatusFromError(nextError));
      throw nextError;
    }
  }, [classId, readSetup, schoolId]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const searchTeachers = useCallback(async (query) => {
    const normalized = query.trim().toLowerCase();
    if (normalized.length < 2) return [];
    const rows = await searchClassroomHandoverTeachers(schoolId, query.trim());
    return (Array.isArray(rows) ? rows : rows?.accounts || []).map(normalizeTeacherAccount);
  }, [schoolId]);

  const inviteTeacher = useCallback(async ({ name, email }) => {
    const existing = await searchTeachers(email);
    const normalizedEmail = email.trim().toLowerCase();
    const exact = existing.find((teacher) => teacher.email.toLowerCase() === normalizedEmail);
    if (exact) return { teacher: exact, existing: true };

    const response = await inviteClassroomHandoverTeacher(schoolId, { name, email });
    const result = response?.results?.[0];
    if (!result || result.status === "failed" || result.status === "invalid_email") {
      const invitationError = new Error(result?.message || "The invitation could not be sent.");
      invitationError.code = result?.status || "failed";
      throw invitationError;
    }
    if (result.status === "existing_active_account" || result.status === "duplicate_pending_teacher") {
      const matches = await searchTeachers(email);
      const matchedTeacher = matches.find((teacher) => teacher.email.toLowerCase() === normalizedEmail);
      if (matchedTeacher) return { teacher: matchedTeacher, existing: true };
    }
    return {
      teacher: {
        id: result.pendingteacherid,
        name: name.trim(),
        email: normalizedEmail,
        kind: "pending",
        status: result.status,
      },
      existing: false,
    };
  }, [schoolId, searchTeachers]);

  const submit = useCallback(async ({ className, teacher }) => {
    if (!entry) throw new Error("Setup is not ready");
    const payload = {
      className: className.trim(),
      teacherId: teacher?.kind === "active" ? teacher.id : null,
      pendingTeacherId: teacher?.kind === "pending" ? teacher.id : null,
      expectedVersion: entry.version,
    };
    const signature = JSON.stringify(payload);
    if (attemptRef.current?.signature !== signature) {
      attemptRef.current = {
        signature,
        key: getSetupContinueIdempotencyKey(classId, entry.version, payload),
      };
    }
    const request = { ...payload, idempotencyKey: attemptRef.current.key };
    setStatus("saving");
    setError(null);
    try {
      const saved = normalizeClassroomHandoverSetup(
        await continueClassroomHandoverSetup(schoolId, classId, request)
      );
      setEntry(saved);
      setStatus("ready");
      attemptRef.current = null;
      return saved;
    } catch (nextError) {
      setError(nextError);
      if (nextError?.response?.status === 409) {
        try {
          setConflictEntry(normalizeClassroomHandoverSetup(await readSetup()));
        } catch (_) {
          setConflictEntry(null);
        }
      }
      setStatus(setupStatusFromError(nextError));
      throw nextError;
    }
  }, [classId, entry, readSetup, schoolId]);

  const resumeLatest = useCallback(() => {
    if (conflictEntry) setEntry(conflictEntry);
    setConflictEntry(null);
    setError(null);
    setStatus("ready");
    attemptRef.current = null;
  }, [conflictEntry]);

  return {
    entry,
    status,
    error,
    conflictEntry,
    load,
    searchTeachers,
    inviteTeacher,
    submit,
    resumeLatest,
  };
}
